/**
 * Loads below-fold Shopify sections on demand via the Section Rendering API.
 */
(function () {
  const hosts = document.querySelectorAll("[data-lazy-section-id]");
  if (!hosts.length) return;

  const loadedSections = new Set();

  /**
   * Re-runs script tags injected through innerHTML.
   * @param {HTMLElement} root
   */
  const activateScripts = (root) => {
    root.querySelectorAll("script").forEach((oldScript) => {
      const script = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
      });
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  };

  /**
   * Fetches and injects section HTML for a lazy host element.
   * @param {HTMLElement} host
   */
  const loadSection = async (host, variantOverride = "") => {
    const sectionId = host.dataset.lazySectionId;
    if (!sectionId || loadedSections.has(sectionId)) return;

    loadedSections.add(sectionId);
    host.setAttribute("data-lazy-section-loading", "true");

    const params = new URLSearchParams(window.location.search);
    const variantId =
      variantOverride || params.get("variant") || host.dataset.variantId || "";
    const fetchParams = new URLSearchParams({ section_id: sectionId });
    if (variantId) fetchParams.set("variant", variantId);

    const url = `${window.location.pathname}?${fetchParams.toString()}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);

      const html = await response.text();
      host.innerHTML = html;
      activateScripts(host);
      host.setAttribute("data-lazy-section-loaded", "true");
    } catch (error) {
      console.error("Lazy section load failed:", sectionId, error);
      loadedSections.delete(sectionId);
    } finally {
      host.removeAttribute("data-lazy-section-loading");
    }
  };

  /**
   * Schedules section load for eager hosts after initial paint.
   * @param {HTMLElement} host
   */
  const scheduleEagerLoad = (host) => {
    const run = () => loadSection(host);
    if ("requestIdleCallback" in window) {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 1);
    }
  };

  const eagerHosts = [];
  const deferredHosts = [];

  hosts.forEach((host) => {
    const urlParams = new URLSearchParams(window.location.search);
    if (host.dataset.lazySectionEager === "true" || urlParams.get("variant")) {
      eagerHosts.push(host);
    } else {
      deferredHosts.push(host);
    }
  });

  eagerHosts.forEach(scheduleEagerLoad);

  if (!deferredHosts.length) return;

  if (!("IntersectionObserver" in window)) {
    deferredHosts.forEach(loadSection);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        loadSection(entry.target);
      });
    },
    { rootMargin: "300px 0px" },
  );

  deferredHosts.forEach((host) => observer.observe(host));

  document.addEventListener("variant-url-updated", (event) => {
    const variantId =
      event.detail?.variantId ||
      new URLSearchParams(window.location.search).get("variant");

    if (!variantId) return;

    deferredHosts.forEach((host) => {
      if (host.dataset.lazySectionLoaded === "true") return;
      observer.unobserve(host);
      loadSection(host, variantId);
    });
  });
})();
